import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import DeleteIcon from '@material-ui/icons/Delete';

import Request from '~/providers/Request';
import Button from '~/components/Button';
import IconButton from '~/components/Button/IconButton';
import InputControl from '~/components/Input/InputControl';
import InputSelect from '~/components/Input/InputSelect';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';

const useStyles = makeStyles(() => ({
  title: { fontSize: 13, fontWeight: 500, color: 'rgba(0, 0, 0, 0.87)' },
  table: {
    marginTop: 16,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  head: { backgroundColor: '#f5f5f5' },
}));

const sameLink = (a, b) =>
  Number(a.specialty_id) === Number(b.specialty_id) &&
  Number(a.doctor_id) === Number(b.doctor_id);

function LinkedDoctors({ links, specialtyOptions, onChange }) {
  const classes = useStyles();
  const [specialty, setSpecialty] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [exclusive, setExclusive] = useState(false);
  const [doctorOptions, setDoctorOptions] = useState([]);

  useEffect(() => {
    setDoctors([]);
    setDoctorOptions([]);
    if (!specialty) return;

    Request.getNotDeserialize(
      `/admin/schedules/doctors_by_specialty?specialty_id=${specialty.id}`,
      null,
      { normalizeJSON: true }
    )
      .then((response) =>
        setDoctorOptions(
          response.data
            .map((doctor) => ({
              id: Number(doctor.user_id),
              name: String(doctor.user_name || '').trim(),
            }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        )
      )
      .catch(() => setDoctorOptions([]));
  }, [specialty && specialty.id]);

  const handleAdd = () => {
    if (!specialty || !doctors || !doctors.length) return;

    const added = doctors
      .map((doctor) => ({
        specialty_id: Number(specialty.id),
        specialty_name: String(specialty.name).trim(),
        doctor_id: Number(doctor.id),
        doctor_name: doctor.name,
        is_exclusive: exclusive,
      }))
      .filter((link) => !links.some((item) => sameLink(item, link)));

    onChange([...links, ...added]);
    setDoctors([]);
    setExclusive(false);
  };

  const toggleExclusive = (link) =>
    onChange(
      links.map((item) =>
        sameLink(item, link) ? { ...item, is_exclusive: !item.is_exclusive } : item
      )
    );

  const remove = (link) => onChange(links.filter((item) => !sameLink(item, link)));

  return (
    <GridContainer spacing={2} style={{ marginTop: 16 }}>
      <GridItem xs={12} md={12}>
        <Typography className={classes.title}>Profissionais vinculados</Typography>
      </GridItem>
      <GridItem xs={12} md={4}>
        <InputControl
          name="linked_specialty"
          label="Especialidade"
          value={specialty}
          inputComponent={InputSelect}
          onChange={({ linked_specialty }) => setSpecialty(linked_specialty)}
          InputProps={{ options: specialtyOptions }}
        />
      </GridItem>
      <GridItem xs={12} md={4}>
        <InputControl
          name="linked_doctors"
          label="Profissionais"
          value={doctors}
          inputComponent={InputSelect}
          onChange={({ linked_doctors }) => setDoctors(linked_doctors || [])}
          InputProps={{
            isClearable: true,
            isMulti: true,
            options: doctorOptions,
          }}
        />
      </GridItem>
      <GridItem xs={6} md={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={exclusive}
              onChange={(event) => setExclusive(event.target.checked)}
            />
          }
          label="Exclusivo"
        />
      </GridItem>
      <GridItem xs={6} md={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
        <Button
          color="primary"
          disabled={!specialty || !doctors || !doctors.length}
          onClick={handleAdd}
        >
          Adicionar
        </Button>
      </GridItem>
      <GridItem xs={12} md={12}>
        {links.length > 0 && (
          <div className={classes.table}>
            <Table size="small">
              <TableHead className={classes.head}>
                <TableRow>
                  <TableCell>Profissional</TableCell>
                  <TableCell>Especialidade</TableCell>
                  <TableCell>Exclusivo</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {links.map((link) => (
                  <TableRow key={`${link.specialty_id}-${link.doctor_id}`}>
                    <TableCell>{link.doctor_name}</TableCell>
                    <TableCell>{link.specialty_name}</TableCell>
                    <TableCell>
                      <Checkbox
                        color="primary"
                        checked={!!link.is_exclusive}
                        onChange={() => toggleExclusive(link)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        Icon={DeleteIcon}
                        iconColor="red"
                        tooltip="Remover"
                        onClick={() => remove(link)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </GridItem>
    </GridContainer>
  );
}

LinkedDoctors.propTypes = {
  links: PropTypes.array.isRequired,
  specialtyOptions: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default LinkedDoctors;
